import { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useDispatch, useSelector } from 'react-redux';
import { useWatch } from 'react-hook-form';

import {
  CountrySelectFormInput,
  FlexSalesForm,
  FlexSalesFormActions,
  StoreSelectFormInput,
  TextFieldFormInput,
  useFlexSalesForm,
} from '../FlexSalesForm';
import {
  masterDataDataSelector,
  parametricStoresDefaultCountrySelector,
  parametricStoresEnabledCountriesSelector,
  parametricStoresSaveSelector,
} from '../../state/selectors';
import { getSchema, ParametricStoreFormValues } from './schema';
import { parametricStoresCreateRequested, parametricStoresUpdateRequested } from '../../state';
import { ParametricStore } from '../../state/parametricStores/types';
import { Stores } from '../../types';

interface Props {
  editItem?: ParametricStore;
  onClose: () => void;
  /** El pais que se esta mirando en la pantalla: el alta arranca con ese. */
  country?: string;
}

// El desplegable de local es el mismo que usa el resto de la aplicacion: trae los locales del
// pais elegido en este formulario. Al elegir uno, completa solo el nombre desde el maestro.
const StoreFieldByCountry = ({ stores }: { stores: Stores[] }) => {
  const { control, setValue } = useFlexSalesForm<ParametricStoreFormValues>();
  const countryValue = useWatch<ParametricStoreFormValues>({ control, name: 'country' });
  const storeValue = useWatch<ParametricStoreFormValues>({ control, name: 'store' });
  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    if (storeValue) {
      const match = stores.find(
        i => i.countryCode === countryValue && i.storeAcronym === storeValue
      );
      // con shouldValidate, porque el boton de guardar se habilita por el estado de
      // validacion y completar el nombre por codigo no dispara la validacion solo.
      if (match)
        setValue('store_name', match.name as never, {
          shouldValidate: true,
          shouldDirty: true,
        });
    }
  }, [storeValue, countryValue, stores, setValue]);

  return (
    <StoreSelectFormInput
      name="store"
      countryCode={(countryValue as string) || ''}
      fullWidth
      required
    />
  );
};

const ParametricStoresForm = ({ editItem, onClose, country }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const saveState = useSelector(parametricStoresSaveSelector);
  const masterData = useSelector(masterDataDataSelector);
  const resolver = useMemo(() => yupResolver(getSchema()), []);
  const submitted = useRef(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const defaultCountry = useSelector(parametricStoresDefaultCountrySelector);
  const enabledCountries = useSelector(parametricStoresEnabledCountriesSelector);

  const defaultValues: ParametricStoreFormValues = {
    country: editItem?.country ?? country ?? defaultCountry,
    code: editItem?.code ?? '',
    store: editItem?.store ?? '',
    store_name: editItem?.store_name ?? '',
  };

  useEffect(() => {
    if (!submitted.current) return;
    if (saveState.loaded && !saveState.failure) {
      onClose();
      return;
    }
    if (saveState.failure && saveState.error) {
      const msg = (saveState.error.data as any)?.message as string | undefined;
      setSubmitError(
        msg?.includes('already exists')
          ? t('parametricStores.form.errorCodeExists')
          : t('parametricStores.form.saveError')
      );
    }
  }, [saveState.loaded, saveState.failure, saveState.error, onClose, t]);

  const onSubmit = (values: ParametricStoreFormValues) => {
    submitted.current = true;
    setSubmitError(null);
    if (editItem) {
      dispatch(
        parametricStoresUpdateRequested(editItem.country, editItem.code, {
          store: values.store,
          store_name: values.store_name,
        })
      );
    } else {
      dispatch(
        parametricStoresCreateRequested({
          country: values.country,
          code: values.code,
          store: values.store,
          store_name: values.store_name,
        })
      );
    }
  };

  return (
    <FlexSalesForm<ParametricStoreFormValues>
      onValidSubmit={onSubmit}
      resolver={resolver}
      defaultValues={defaultValues}
    >
      <Grid container flexDirection="column" rowSpacing={2}>
        <Grid item xs>
          <CountrySelectFormInput
            name="country"
            allowedCountryCodes={enabledCountries}
            fullWidth
            required
            disabled={Boolean(editItem)}
          />
        </Grid>
        <Grid item xs>
          <TextFieldFormInput<ParametricStoreFormValues>
            name="code"
            label={t('parametricStores.form.code')}
            fullWidth
            disabled={Boolean(editItem)}
            required
          />
        </Grid>
        <Grid item xs>
          <StoreFieldByCountry stores={masterData?.stores ?? []} />
        </Grid>
        <Grid item xs>
          <TextFieldFormInput<ParametricStoreFormValues>
            name="store_name"
            label={t('parametricStores.form.store_name')}
            fullWidth
            required
          />
        </Grid>
        {submitError && (
          <Grid item xs>
            <Alert severity="error">{submitError}</Alert>
          </Grid>
        )}
        <Grid item xs>
          <FlexSalesFormActions
            onCancel={onClose}
            cancelLabel={t('parametricStores.form.cancel')}
            submitLabel={t('parametricStores.form.save')}
          />
        </Grid>
      </Grid>
    </FlexSalesForm>
  );
};

export default ParametricStoresForm;
